import ReadMoreText from "components/ReadMoreText";
import FileRow from "components/file-components/file-row";

interface InvestigationDisplayProps {
  investigationSummary?: string;
  investigationFiles?: any[];
  prosecutorDecisionSummary?: string | null;
  prosecutorDecisionFiles?: any[] | null;
  fileRowProps: any;
  application_type?: string | null;
  administrative_decision_summary?: string | null;
  administrative_decision_date?: string | null;
  administrative_decision_documents?: File[] | null;
  suspect_asset_counts: Array<{
    id: string;
    asset_suspects: Array<{
      id: string;
      person: {
        full_name: string;
        [key: string]: any;
      };
      [key: string]: any;
    }>;
  }>;
}

// Function to extract suspect names from the suspect_asset_counts array
const extractSuspectNames = (
  suspectAssetCounts: InvestigationDisplayProps["suspect_asset_counts"],
): string[] => {
  if (!suspectAssetCounts || suspectAssetCounts.length === 0) return [];

  const names: string[] = [];

  suspectAssetCounts.forEach((item) => {
    if (item.asset_suspects && Array.isArray(item.asset_suspects)) {
      item.asset_suspects.forEach((suspect) => {
        if (suspect.person?.full_name) {
          names.push(suspect.person.full_name);
        }
      });
    }
  });

  // Remove duplicates if any (using Set)
  return [...new Set(names)];
};

// Array of vibrant colors for the badges
const badgeColors = [
  "bg-blue-100 text-blue-800 border-blue-200",
  "bg-green-100 text-green-800 border-green-200",
  "bg-purple-100 text-purple-800 border-purple-200",
  "bg-pink-100 text-pink-800 border-pink-200",
  "bg-indigo-100 text-indigo-800 border-indigo-200",
  "bg-yellow-100 text-yellow-800 border-yellow-200",
  "bg-red-100 text-red-800 border-red-200",
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-orange-100 text-orange-800 border-orange-200",
  "bg-cyan-100 text-cyan-800 border-cyan-200",
];

export default function InvestigationDisplay({
  investigationSummary,
  investigationFiles,
  prosecutorDecisionSummary,
  prosecutorDecisionFiles,
  fileRowProps,
  administrative_decision_date,
  administrative_decision_summary,
  administrative_decision_documents,
  application_type,
  suspect_asset_counts,
}: InvestigationDisplayProps) {
  const suspectNames = extractSuspectNames(suspect_asset_counts);

  return (
    <div className="space-y-4">
      {/* Suspect Names Display - New Section */}
      {suspectNames.length > 0 && (
        <div className="flex flex-col">
          <label className="mb-2 referral-form-label">
            Suspect{suspectNames.length > 1 ? "s" : ""} ({suspectNames.length})
          </label>
          <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded border">
            {suspectNames.map((name, index) => {
              // Assign a color based on index to keep consistent colors per suspect
              const colorIndex = index % badgeColors.length;
              return (
                <div
                  key={`${name}-${index}`}
                  className={`
                    inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium
                    border ${badgeColors[colorIndex]} 
                    shadow-sm hover:shadow-md transition-shadow
                    break-all max-w-full
                  `}
                >
                  <span className="truncate">{name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Investigation Summary Display */}
      <div className="flex flex-col">
        <label className="mb-2 referral-form-label">
          Investigation Summary
        </label>
        <div className="p-3 bg-gray-50 rounded border min-h-[80px]">
          {investigationSummary ? (
            <p className="text-gray-700 whitespace-pre-wrap">
              <ReadMoreText text={investigationSummary} maxLines={3} />
            </p>
          ) : (
            <p className="text-gray-400">No summary provided</p>
          )}
        </div>
      </div>

      {/* Investigation File Display */}
      <div className="flex flex-col">
        <label className="mb-2 referral-form-label">Investigation File</label>
        {investigationFiles && investigationFiles.length > 0 ? (
          <div className="space-y-2">
            {investigationFiles.map((file: any, index: number) => (
              <FileRow
                key={file.document_uuid || index}
                file={file}
                {...fileRowProps}
              />
            ))}
          </div>
        ) : (
          <div className="p-3 bg-gray-50 rounded border text-gray-400">
            No file attached
          </div>
        )}
      </div>

      {/* Prosecutor Decision Summary */}
      {prosecutorDecisionSummary && (
        <div className="flex flex-col">
          <label className="mb-2 referral-form-label">
            Prosecutor Choice Summary
          </label>
          <div className="p-3 bg-gray-50 rounded border min-h-[80px]">
            <p className="text-gray-700 whitespace-pre-wrap">
              <ReadMoreText text={prosecutorDecisionSummary} maxLines={3} />
            </p>
          </div>
        </div>
      )}

      {/* Prosecutor Decision Files */}
      {prosecutorDecisionFiles && prosecutorDecisionFiles.length > 0 && (
        <div className="flex flex-col">
          <label className="mb-2 referral-form-label">Decision File</label>
          <div className="space-y-2">
            {prosecutorDecisionFiles.map((file: any, index: number) => (
              <FileRow
                key={file.document_uuid || index}
                file={file}
                {...fileRowProps}
              />
            ))}
          </div>
        </div>
      )}

      {/* Application Type */}
      {application_type && (
        <div className="flex flex-col">
          <label className="mb-2 referral-form-label">Application Type</label>
          <div className="p-3 bg-gray-50 rounded border">
            <p className="text-gray-700 whitespace-pre-wrap">
              {application_type}
            </p>
          </div>
        </div>
      )}

      {/* Administrative Decision Summary */}
      {administrative_decision_summary && (
        <div className="flex flex-col">
          <label className="mb-2 referral-form-label">
            Administrative Decision Summary
          </label>
          <div className="p-3 bg-gray-50 rounded border min-h-[80px]">
            <p className="text-gray-700 whitespace-pre-wrap">
              <ReadMoreText
                text={administrative_decision_summary}
                maxLines={3}
              />
            </p>
          </div>
        </div>
      )}

      {/* Administrative Decision Date */}
      {administrative_decision_date && (
        <div className="flex flex-col">
          <label className="mb-2 referral-form-label">
            Administrative Decision Date
          </label>
          <div className="p-3 bg-gray-50 rounded border">
            <p className="text-gray-700 whitespace-pre-wrap">
              {administrative_decision_date.split("T")[0]}
            </p>
          </div>
        </div>
      )}

      {/* Administrative Decision Documents */}
      {administrative_decision_documents &&
        administrative_decision_documents.length > 0 && (
          <div className="flex flex-col">
            <label className="mb-2 referral-form-label">Decision File</label>
            <div className="space-y-2">
              {administrative_decision_documents.map(
                (file: any, index: number) => (
                  <FileRow
                    key={file.document_uuid || index}
                    file={file}
                    {...fileRowProps}
                  />
                ),
              )}
            </div>
          </div>
        )}
    </div>
  );
}
